import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Wallet, ChevronDown, ChevronUp, Landmark, AlertTriangle } from "lucide-react";
import { BankAccountDialog } from "@/components/BankAccountDialog";
import { StatementDownload } from "@/components/StatementDownload";
import { TransfersList } from "@/components/PayoutReceipts";

const money = (v) => `${Number(v || 0).toFixed(2)} ر.س`;
const fmt = (s) => (s ? new Date(s).toLocaleDateString("ar-SA-u-nu-latn", { dateStyle: "medium" }) : "—");

const Stat = ({ label, value, cls = "", testid }) => (
  <div className="rounded-xl bg-muted/40 p-3">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`text-lg font-extrabold mt-0.5 ${cls}`} data-testid={testid}>{value}</div>
  </div>
);

export function PartnerEarnings() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  useEffect(() => { api.get("/my/earnings").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  if (!d) return null;
  const rate = d.items[0] ? Math.round(d.items[0].commission_rate * 100) : null;

  return (
    <Card className="p-5 mb-8 space-y-4" data-testid="partner-earnings">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-bold flex items-center gap-2"><Wallet className="w-5 h-5 text-primary" /> أرباحي ومستحقاتي</h3>
        {d.items.length > 0 && (
          <Button size="sm" variant="ghost" className="gap-1" onClick={() => setOpen(!open)} data-testid="earnings-toggle">
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />} تفاصيل العمليات ({d.count})
          </Button>
        )}
      </div>
      <StatementDownload />
      {d.bank ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border p-3 text-sm" data-testid="bank-summary">
          <div className="flex items-center gap-2"><Landmark className="w-4 h-4 text-primary" /> {d.bank.bank_name} · <bdi dir="ltr" className="font-mono" data-testid="bank-iban-display">{d.bank.iban}</bdi></div>
          <Button size="sm" variant="ghost" onClick={() => setBankOpen(true)} data-testid="bank-edit-btn">تعديل</Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-800" data-testid="bank-missing-alert">
          <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> أضف اسم البنك ورقم الآيبان حتى نحوّل لك مستحقاتك.</div>
          <Button size="sm" className="rounded-full" onClick={() => setBankOpen(true)} data-testid="bank-add-btn">إضافة الحساب البنكي</Button>
        </div>
      )}
      <BankAccountDialog open={bankOpen} onOpenChange={setBankOpen} bank={d.bank} onSaved={(b) => setD({ ...d, bank: b })} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="إجمالي ما دفعه العملاء" value={money(d.gross)} testid="earnings-gross" />
        <Stat label={`عمولة المنصة${rate !== null ? ` (${rate} بالمائة)` : ""}`} value={money(d.commission)} testid="earnings-commission" />
        <Stat label="تم تحويله لك" value={money(d.transferred_net)} cls="text-emerald-700" testid="earnings-transferred" />
        <Stat label="بانتظار التحويل" value={money(d.pending_net)} cls="text-amber-700" testid="earnings-pending" />
      </div>
      <p className="text-xs text-muted-foreground">تُحوّل المستحقات خلال 3 أيام عمل، وتُخصم الرسوم البنكية إذا لم يتجاوز المبلغ المحوّل 2000 ريال.{d.last_transfer_at && ` آخر تحويل: ${fmt(d.last_transfer_at)}`}</p>
      {d.transfers?.length > 0 && (
        <div className="space-y-2" data-testid="partner-transfers">
          <div className="text-sm font-semibold">سجل التحويلات والإيصالات</div>
          <TransfersList transfers={d.transfers} />
        </div>
      )}
      {open && (
        <div className="divide-y divide-border border border-border rounded-xl" data-testid="earnings-items">
          {d.items.map((it) => (
            <div key={it.id} className="p-3 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`earning-row-${it.id}`}>
              <div><div className="font-semibold">{fmt(it.paid_at)}</div><div className="text-xs text-muted-foreground font-mono truncate max-w-[220px]">{it.transaction_id}</div></div>
              <div className="text-xs text-muted-foreground">المبلغ {money(it.amount)} · العمولة {money(it.commission)}</div>
              <div className="font-bold">{money(it.net)}</div>
              {it.payout_status === "transferred"
                ? <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 border-emerald-500/30">حُوّل {fmt(it.transferred_at)}</Badge>
                : <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/30">بانتظار التحويل</Badge>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
